import { Box, Button, IconButton, Typography } from "@mui/material";
import { memo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdAdd, MdDelete } from "react-icons/md";

import { useFieldName, useSchemaForm } from "../context";
import { buildDefault } from "../defaults";
import { ArrayNode } from "../types";
import { FieldLabel } from "./FieldLabel";
import { PrimitiveInput } from "./PrimitiveInput";

/*
 * A list of values.
 * Primitive items are edited in place; other items need the JSON editor.
 * With `bare`, no label (the parent shows the title).
 */
export const ArrayField = memo(
  ({ node, bare = false }: { node: ArrayNode; bare?: boolean }) => {
    const { t } = useTranslation();
    const { errors } = useSchemaForm();
    const { control, setValue } = useFormContext();
    const name = useFieldName(node.path);
    const watched = useWatch({ control, name });
    const items: unknown[] = Array.isArray(watched) ? watched : [];
    const error = errors[node.path];

    const update = (next: unknown[]) =>
      setValue(name, next, { shouldDirty: true });

    return (
      <Box sx={{ mb: 2 }}>
        {!bare && (
          <FieldLabel
            title={node.title}
            description={node.description}
            required={node.required}
          />
        )}

        {items.map((item, index) => (
          <Box
            key={index}
            sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
          >
            {node.item.kind === "primitive" ? (
              <PrimitiveInput
                node={node.item}
                value={item}
                onChange={(value) =>
                  update(items.map((old, i) => (i === index ? value : old)))
                }
                label={t("schemaForm.itemPlaceholder", { n: index + 1 })}
                placeholder={t("schemaForm.itemPlaceholder", { n: index + 1 })}
                errorText={errors[node.path ? `${node.path}.${index}` : index]}
                sx={{ flex: 1 }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t("schemaForm.complexObject")}
              </Typography>
            )}

            <IconButton
              size="medium"
              color="error"
              aria-label={t("common.delete")}
              onClick={() => update(items.filter((_, i) => i !== index))}
            >
              <MdDelete />
            </IconButton>
          </Box>
        ))}

        {error && (
          <Typography variant="caption" color="error" sx={{ display: "block" }}>
            {error}
          </Typography>
        )}

        <Button
          startIcon={<MdAdd />}
          size="small"
          onClick={() => update([...items, buildDefault(node.item, false)])}
          sx={{ mt: -1 }}
        >
          {t("schemaForm.addItem")}
        </Button>
      </Box>
    );
  },
);

ArrayField.displayName = "ArrayField";
